import React, { useEffect, useRef, useState, forwardRef, useImperativeHandle } from 'react';
import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader';
import {
  Box,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  VStack,
  HStack,
  Text,
  Input,
  Select,
  Checkbox
} from '@chakra-ui/react';

// Measurement constants for standardization (must match FloorPlanCanvas.js)
const MEASUREMENT_CONSTANTS = {
  PIXELS_PER_INCH: 1, // 1 pixel = 1 inch for simplicity
  INCHES_PER_FOOT: 12,
  STANDARD_GRID_FEET: 5, // 5 foot grid
  STANDARD_ROOM_FEET: 30, // 30x30 ft standard room
  
  // Calculated values
  get PIXELS_PER_FOOT() { return this.PIXELS_PER_INCH * this.INCHES_PER_FOOT; }, // 12 pixels = 1 foot
  get GRID_PIXELS() { return this.STANDARD_GRID_FEET * this.PIXELS_PER_FOOT; }, // 60 pixels = 5 feet
  get STANDARD_ROOM_PIXELS() { return this.STANDARD_ROOM_FEET * this.PIXELS_PER_FOOT; }, // 360 pixels = 30 feet
  
  // 3D conversion constants
  SCALE_3D_TO_REAL: 0.1, // Scale down for reasonable 3D scene size (1 foot = 0.1 3D units)
  SCALE_3D_TO_REAL_INCH: 0.008333, // Scale down for reasonable 3D scene size (1 inch = 0.008333 3D units)
  
  // Standard architectural measurements in feet
  STANDARD_CEILING_HEIGHT_FEET: 10, // Standard ceiling height
};

/**
 * ThreeDViewer Component
 * 
 * Renders a 3D scene using Three.js with support for:
 * - Rooms, walls, windows, doors, and products
 * - GLB model loading from URLs for products and windows
 * - Consistent ceiling alignment for all 3D models
 * - Loading states and error handling
 * - Camera controls and view modes
 * 
 * Expected product data structure for GLB models:
 * {
 *   id: "product_1",
 *   type: "product",
 *   name: "Ceiling Light",
 *   glbUrl: "https://api.example.com/models/ceiling_light.glb",
 *   x: 100,
 *   y: 200,
 *   width: 50,
 *   height: 50,
 *   // Optional: product-specific configuration
 *   productType: "lighting" // or "furniture", "appliance"
 * }
 * 
 * @param {Object} floorPlanData - Floor plan data containing objects to render
 * @param {Function} onUpdate - Callback when scene is updated
 * @param {number} sidebarWidth - Width of the sidebar for responsive layout
 * @param {boolean} isSidebarCollapsed - Whether sidebar is collapsed
 * @param {React.Ref} ref - Forwarded ref for accessing component methods
 */
const ThreeDViewer = forwardRef(({ floorPlanData, onUpdate, sidebarWidth = 300, isSidebarCollapsed = false }, ref) => {
  const mountRef = useRef(null);
  const sceneRef = useRef(null);
  const rendererRef = useRef(null);
  const cameraRef = useRef(null);
  const controlsRef = useRef(null);
  const [isInitialized, setIsInitialized] = useState(false);
  const [viewMode, setViewMode] = useState('perspective');
  const [cameraHeight, setCameraHeight] = useState(10);
  // Default wall height: 10 feet converted to 3D units
  const [wallHeight, setWallHeight] = useState(
    MEASUREMENT_CONSTANTS.STANDARD_CEILING_HEIGHT_FEET * MEASUREMENT_CONSTANTS.SCALE_3D_TO_REAL
  ); // 10ft = 1.0 3D units
  const [loadingGLBModels, setLoadingGLBModels] = useState(new Set());
  const testCubeRef = useRef(null); // Reference to the test cube for repositioning
  
  // Modal and color picker state
  const [isColorModalOpen, setIsColorModalOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [nickelColor, setNickelColor] = useState('#C0C0C0'); // Default nickel color
  const [needlePunchColor, setNeedlePunchColor] = useState('#FFFFFF'); // Default white
  const [selectedPosition, setSelectedPosition] = useState('full up'); // Default position
  const [applyToAll, setApplyToAll] = useState(false); // Apply to all products checkbox
  const raycasterRef = useRef(new THREE.Raycaster());
  const mouseRef = useRef(new THREE.Vector2());

  // Calculate container dimensions based on sidebar state
  const getContainerDimensions = () => {
    const sidebarW = isSidebarCollapsed ? 50 : sidebarWidth;
    const windowWidth = window.innerWidth;
    const windowHeight = window.innerHeight;
    const headerHeight = 120; // Header + subheader height
    
    return {
      width: windowWidth - sidebarW - 40, // 40px for padding
      height: windowHeight - headerHeight - 40 // 40px for padding
    };
  };

  // Coordinate normalization system
  const normalize2DTo3D = (x, y, canvasWidth, canvasHeight) => {
    // Calculate the 2D canvas center
    const canvasCenterX = canvasWidth / 2;
    const canvasCenterY = canvasHeight / 2;
    
    // Normalize coordinates relative to canvas center
    // Convert from pixels to feet, then scale for 3D scene
    const feetX = (x - canvasCenterX) / MEASUREMENT_CONSTANTS.PIXELS_PER_FOOT;
    const feetZ = (y - canvasCenterY) / MEASUREMENT_CONSTANTS.PIXELS_PER_FOOT;
    
    // Scale for 3D scene (1 foot = 0.1 3D units for reasonable scene size)
    const normalizedX = feetX * MEASUREMENT_CONSTANTS.SCALE_3D_TO_REAL;
    const normalizedZ = feetZ * MEASUREMENT_CONSTANTS.SCALE_3D_TO_REAL; // Y becomes Z in 3D
    
    return { x: normalizedX, z: normalizedZ };
  };

  // Helper function to convert feet to 3D units
  const feetTo3DUnits = (feet) => {
    return feet * MEASUREMENT_CONSTANTS.SCALE_3D_TO_REAL;
  };

  // Helper function to convert 3D units to feet
  const units3DToFeet = (units) => {
    return units / MEASUREMENT_CONSTANTS.SCALE_3D_TO_REAL;
  };

  // Get canvas dimensions for coordinate normalization
  const getCanvasDimensions = () => {
    const sidebarW = isSidebarCollapsed ? 50 : sidebarWidth;
    const windowWidth = window.innerWidth;
    const windowHeight = window.innerHeight;
    const headerHeight = 120; // Header + subheader height
    
    return {
      width: windowWidth - sidebarW - 40, // 40px for padding
      height: windowHeight - headerHeight - 40 // 40px for padding
    };
  };

  // Helper function to get product configuration based on product type
  const getProductConfig = (productData, wallType = null, wallPosition = null, newGLBUrl = null) => {
    // Calculate wall offset to keep products inside the room
    const wallOffsetDistance = MEASUREMENT_CONSTANTS.SCALE_3D_TO_REAL_INCH * 4; // 0.2 3D units offset from wall
    // Set cross offset to half of the object's Y-axis width (height in 2D)
    let wallOffset = { x: 0, y: 0, z: 0 };
    let rotation = { x: 0, y: 0, z: 0 };
    
    if (wallType && wallPosition) {
      switch (wallPosition) {
        case 'north': // Top wall
          wallOffset.z = wallOffsetDistance; // Offset towards inside (positive Z)
          wallOffset.x = MEASUREMENT_CONSTANTS.SCALE_3D_TO_REAL_INCH * 2;
          rotation.y = - Math.PI / 2;
          break;
        case 'south': // Bottom wall  
          wallOffset.z = -wallOffsetDistance; // Offset towards inside (negative Z)
          wallOffset.x = -MEASUREMENT_CONSTANTS.SCALE_3D_TO_REAL_INCH * 2;
          rotation.y = Math.PI / 2;
          break;
        case 'east': // Right wall
          wallOffset.x = -wallOffsetDistance; // Offset towards inside (negative X)
          wallOffset.z = MEASUREMENT_CONSTANTS.SCALE_3D_TO_REAL_INCH * 2;
          rotation.y = Math.PI;
          break;
        case 'west': // Left wall
          wallOffset.x = wallOffsetDistance; // Offset towards inside (positive X)
          wallOffset.z = -MEASUREMENT_CONSTANTS.SCALE_3D_TO_REAL_INCH * 2;
          break;
      }
    }
    
    // Default configuration for all products
    const productId = productData.data?.productId;
    const defaultConfig = {
      maxScale: newGLBUrl ? newGLBUrl.indexOf("bracket4") !== -1 ? 0.5 : newGLBUrl.indexOf("bracket2") !== -1 ? 0.5 : 1 : 0.5,
      // ceilingOffset: - MEASUREMENT_CONSTANTS.SCALE_3D_TO_REAL_INCH * 4 * 3.5,
      ceilingOffset: newGLBUrl ? newGLBUrl.indexOf("bracket4") !== -1 ? - (MEASUREMENT_CONSTANTS.SCALE_3D_TO_REAL_INCH * 4 * 3.5) : newGLBUrl.indexOf("bracket2") !== -1 ? - (wallHeight / 4) : - (wallHeight * 1.7) : - (MEASUREMENT_CONSTANTS.SCALE_3D_TO_REAL_INCH * 4 * 3.5),
      rotation: rotation,
      scaleMultiplier: 1,
      wallOffset: wallOffset,
      wallPosition
    };
    
    // Product-specific configurations
    const productConfigs = {
      'lighting': {
        maxScale: 0.3,
        ceilingOffset: 0.1, // Slightly below ceiling
        rotation: { x: 0, y: 0, z: 0 },
        scaleMultiplier: 1,
        wallOffset: wallOffset
      },
      'furniture': {
        maxScale: 0.8,
        ceilingOffset: 0,
        rotation: { x: 0, y: 0, z: 0 },
        scaleMultiplier: 1,
        wallOffset: wallOffset
      },
      'appliance': {
        maxScale: 0.6,
        ceilingOffset: 0,
        rotation: { x: 0, y: 0, z: 0 },
        scaleMultiplier: 1,
        wallOffset: wallOffset
      }
    };
    
    // Get product type from data or infer from name
    const productType = productData.type || 
                       (productData.name && productData.name.toLowerCase().includes('light') ? 'lighting' : 'furniture');
    
    return productConfigs[productType] || defaultConfig;
  };

  // Helper function to align GLB models consistently to ceiling
  const alignGLBToCeiling = (glbObject, targetPosition, wallHeight, productConfig = {}) => {
    // Calculate bounding box to determine model dimensions
    const box = new THREE.Box3().setFromObject(glbObject);
    const size = box.getSize(new THREE.Vector3());
    const center = box.getCenter(new THREE.Vector3());
    
    // Get configuration for this product type
    const {
      maxScale = 0.5,           // Maximum scale for the model
      ceilingOffset = 0,         // Offset from ceiling (positive = below ceiling)
      rotation = { x: 0, y: 0, z: 0 }, // Custom rotation
      scaleMultiplier = 1,       // Additional scale multiplier
      wallOffset = { x: 0, y: 0, z: 0 }, // Wall offset to keep inside room
      wallPosition = "north"
    } = productConfig;
    
    // Scale the model to a reasonable size
    const maxDimension = Math.max(size.x, size.y, size.z);
    const baseScale = maxScale / maxDimension;
    const finalScale = baseScale * scaleMultiplier;
    glbObject.scale.setScalar(finalScale);
    
    // Calculate scaled size after scaling
    const scaledSize = size.clone().multiplyScalar(finalScale);
    
    // Position the model at the target position with wall offset
    // Account for GLB origin point by offsetting half the width across the wall
    let positionX = targetPosition.x + wallOffset.x;
    let positionZ = targetPosition.z + wallOffset.z;
    
    // Adjust for GLB origin point (top-right corner) by offsetting half the model width
    // The offset direction depends on the wall orientation
    if (wallPosition === 'east' || wallPosition === 'west') {
      // Vertical wall (east/west) - offset in Z direction
      positionZ += wallOffset.x > 0 ? -scaledSize.z / 2 : scaledSize.z / 2;
    } else if (wallPosition === 'north' || wallPosition === 'south') {
      // Horizontal wall (north/south) - offset in X direction  
      positionX += wallOffset.z > 0 ? scaledSize.z / 2 : -scaledSize.z / 2;
    }
    
    glbObject.position.set(positionX, wallHeight, positionZ);
    
    // Center the model vertically on the ceiling with offset
    glbObject.position.y = wallHeight - (scaledSize.y / 2) - ceilingOffset;
    
    // Apply custom rotation if specified
    glbObject.rotation.set(rotation.x, rotation.y, rotation.z);
    
    return { scale: finalScale, scaledSize };
  };

  // Helper function to determine wall position (north, south, east, west) based on product position
  const determineWallPosition = (productData, objects) => {
    // Find the room that contains this product
    const rooms = objects.filter(obj => obj && obj.type === 'room');
    if (!rooms.length) return null;
    
    // Get product position (handle both circle and rectangle formats)
    let productX, productY;
    if (productData.radius) {
      productX = productData.x + productData.radius;
      productY = productData.y + productData.radius;
    } else {
      productX = productData.x + (productData.width || 0) / 2;
      productY = productData.y + (productData.height || 0) / 2;
    }
    
    // Find which room contains this product
    const containingRoom = rooms.find(room => {
      return productX >= room.x && productX <= room.x + room.width &&
             productY >= room.y && productY <= room.y + room.height;
    });
    
    if (!containingRoom) return null;
    
    // Determine which wall is closest to the product
    const distanceToTop = Math.abs(productY - containingRoom.y);
    const distanceToBottom = Math.abs(productY - (containingRoom.y + containingRoom.height));
    const distanceToLeft = Math.abs(productX - containingRoom.x);
    const distanceToRight = Math.abs(productX - (containingRoom.x + containingRoom.width));
    
    const minDistance = Math.min(distanceToTop, distanceToBottom, distanceToLeft, distanceToRight);
    
    if (minDistance === distanceToTop) return 'north';
    if (minDistance === distanceToBottom) return 'south'; 
    if (minDistance === distanceToLeft) return 'west';
    if (minDistance === distanceToRight) return 'east';
    
    return null;
  };

  // Handle mouse clicks for product selection
  const handleMouseClick = (event) => {
    if (!rendererRef.current || !cameraRef.current) return;

    const rect = rendererRef.current.domElement.getBoundingClientRect();
    mouseRef.current.x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
    mouseRef.current.y = -((event.clientY - rect.top) / rect.height) * 2 + 1;

    raycasterRef.current.setFromCamera(mouseRef.current, cameraRef.current);

    const scene = sceneRef.current;
    if (!scene) return;

    // Find all GLB product objects in the scene
    const productObjects = [];
    scene.traverse((child) => {
      if (child.userData.isFloorPlanObject && 
          child.userData.type === 'product' && 
          child.userData.isGLB) {
        productObjects.push(child);
      }
    });

    const intersects = raycasterRef.current.intersectObjects(productObjects, true);

    if (intersects.length > 0) {
      // Find the top-level product object
      let productObject = intersects[0].object;
      while (productObject.parent && !productObject.userData.isFloorPlanObject) {
        productObject = productObject.parent;
      }

      if (productObject.userData.type === 'product') {
        console.log('Product clicked:', productObject.userData.originalData);
        
        // Log available materials when product is selected
        const availableMaterials = getAvailableMaterials(productObject);
        console.log('Available materials in clicked product:', Array.from(availableMaterials.values()));
        
        setSelectedProduct(productObject);
        // Set position select to current product position
        const currentPosition = productObject.userData.currentPosition || 'full up';
        setSelectedPosition(currentPosition);
        // Reset apply to all checkbox
        setApplyToAll(false);
        setIsColorModalOpen(true);
      }
    }
  };

  // Function to apply material colors (handles PBR materials with albedo)
  const applyMaterialColors = (productObject, nickelColor, needlePunchColor) => {
    if (!productObject) return;

    productObject.traverse((child) => {
      if (child.isMesh && child.material) {
        const materials = Array.isArray(child.material) ? child.material : [child.material];
        
        materials.forEach((material) => {
          console.log(`Checking material: "${material.name}" (Type: ${material.type})`);
          
          if (material.name === 'Nickel') {
            const newColor = new THREE.Color(nickelColor);
            
            // Apply color to different material properties based on material type
            if (material.color) {
              material.color.copy(newColor);
            }
            
            // For PBR materials, also check for albedo-related properties
            if (material.type === 'MeshStandardMaterial' || material.type === 'MeshPhysicalMaterial') {
              // In Three.js PBR materials, the base color is the 'color' property (equivalent to albedo)
              material.color.copy(newColor);
              
              // If there's an albedo map, we might want to tint it or replace it
              if (material.map) {
                console.log(`Nickel material has albedo map, applying color tint`);
              }
            }
            
            material.needsUpdate = true;
            console.log(`Applied color ${nickelColor} to Nickel material (${material.type})`);
            
          } else if (material.name === 'NeedlePunchWhite') {
            const newColor = new THREE.Color(needlePunchColor);
            
            // Apply color to different material properties based on material type
            if (material.color) {
              material.color.copy(newColor);
            }
            
            // For PBR materials, also check for albedo-related properties
            if (material.type === 'MeshStandardMaterial' || material.type === 'MeshPhysicalMaterial') {
              // In Three.js PBR materials, the base color is the 'color' property (equivalent to albedo)
              material.color.copy(newColor);
              
              // If there's an albedo map, we might want to tint it or replace it
              if (material.map) {
                console.log(`NeedlePunchWhite material has albedo map, applying color tint`);
              }
            }
            
            material.needsUpdate = true;
            console.log(`Applied color ${needlePunchColor} to NeedlePunchWhite material (${material.type})`);
          }
        });
      }
    });
  };

  // Get all available materials in a product
  const getAvailableMaterials = (productObject) => {
    const materials = new Map();
    
    if (!productObject) return materials;
    
    productObject.traverse((child) => {
      if (child.isMesh && child.material) {
        const childMaterials = Array.isArray(child.material) ? child.material : [child.material];
        
        childMaterials.forEach((material) => {
          if (material.name && !materials.has(material.name)) {
            materials.set(material.name, {
              name: material.name,
              type: material.type,
              currentColor: material.color ? `#${material.color.getHexString()}` : 'none',
              hasAlbedoMap: !!material.map
            });
          }
        });
      }
    });
    
    return materials;
  };

  // Get all product objects in the scene
  const getAllProductObjects = () => {
    const scene = sceneRef.current;
    if (!scene) return [];

    const productObjects = [];
    scene.traverse((child) => {
      if (child.userData.isFloorPlanObject && 
          child.userData.type === 'product' && 
          child.userData.isGLB) {
        productObjects.push(child);
      }
    });

    return productObjects;
  };

  // Function to animate fabric geometry based on position
  const updateFabricPosition = (productObject, position) => {
    if (!productObject) return;

    const scene = sceneRef.current;
    if (!scene) return;

    const productId = productObject.userData.productId;
    
    // Find the fabric object associated with this product
    let fabricObject = null;
    scene.traverse((child) => {
      if (child.userData.isPocket && 
          child.userData.productId === productId && 
          child.geometry && 
          child.geometry.parameters) {
        // Check if this is the fabric object (has the fabric dimensions)
        const params = child.geometry.parameters;
        if (params.height && params.height > 0.01) { // Fabric has larger height
          fabricObject = child;
        }
      }
    });

    if (!fabricObject) {
      console.log('Fabric object not found for product:', productId);
      return;
    }

    console.log('Animating fabric geometry for product:', productId, 'to position:', position);

    // Get current geometry parameters
    const currentGeometry = fabricObject.geometry;
    const fabricLength = currentGeometry.parameters.width;
    const fabricDepth = currentGeometry.parameters.depth;
    const currentThickness = currentGeometry.parameters.height;
    
    // Calculate target fabric thickness (height) based on position
    let targetFabricThickness;

    switch (position) {
      case 'full up':
        targetFabricThickness = MEASUREMENT_CONSTANTS.SCALE_3D_TO_REAL_INCH * 4; // Original thickness
        break;
      case 'half way':
        targetFabricThickness = MEASUREMENT_CONSTANTS.SCALE_3D_TO_REAL * 5; // 5 feet
        break;
      case 'full down':
        targetFabricThickness = MEASUREMENT_CONSTANTS.SCALE_3D_TO_REAL * 10; // 10 feet
        break;
      default:
        targetFabricThickness = MEASUREMENT_CONSTANTS.SCALE_3D_TO_REAL_INCH * 4; // Original thickness
    }

    // If already at target thickness, no animation needed
    if (Math.abs(currentThickness - targetFabricThickness) < 0.001) {
      console.log('Fabric already at target thickness');
      productObject.userData.currentPosition = position;
      return;
    }

    // Cancel any existing animation for this fabric
    if (fabricObject.userData.animationId) {
      cancelAnimationFrame(fabricObject.userData.animationId);
    }

    // Animation parameters
    const animationDuration = 5000; // 5 seconds
    const startTime = Date.now();
    const startThickness = currentThickness;
    const thicknessDifference = targetFabricThickness - startThickness;
    
    console.log(`Animating from ${startThickness} to ${targetFabricThickness} over ${animationDuration}ms`);

    // Easing function (ease-in-out)
    const easeInOut = (t) => {
      return t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t;
    };

    // Animation function
    const animateThickness = () => {
      const elapsed = Date.now() - startTime;
      const progress = Math.min(elapsed / animationDuration, 1);
      const easedProgress = easeInOut(progress);
      
      // Calculate current thickness
      const currentAnimatedThickness = startThickness + (thicknessDifference * easedProgress);
      
      // Create new geometry with animated thickness
      const animatedGeometry = new THREE.BoxGeometry(fabricLength, currentAnimatedThickness, fabricDepth);
      
      // Dispose of old geometry
      fabricObject.geometry.dispose();
      
      // Apply new geometry
      fabricObject.geometry = animatedGeometry;
      
      // Adjust Y position based on current thickness
      const baseY = productObject.position.y - 0.035 + (MEASUREMENT_CONSTANTS.SCALE_3D_TO_REAL_INCH * 2);
      fabricObject.position.y = baseY - (currentAnimatedThickness / 2);
      
      // Continue animation if not complete
      if (progress < 1) {
        fabricObject.userData.animationId = requestAnimationFrame(animateThickness);
      } else {
        // Animation complete
        console.log(`Animation complete: final thickness = ${currentAnimatedThickness}`);
        fabricObject.userData.animationId = null;
        productObject.userData.currentPosition = position;
      }
    };

    // Start animation
    animateThickness();
  };

  // Get GLB URL based on selected position
  const getGLBUrlForPosition = (position) => {
    const urls = {
      'full up': 'https://api.daylighting360.com/bracket4.glb',
      'half way': 'https://api.daylighting360.com/bracket2.glb',
      'full down': 'https://api.daylighting360.com/bracket3.glb'
    };
    return urls[position] || urls['full up'];
  };

  // Function to reload GLB model with new URL
  const reloadGLBModel = (productObject, newGLBUrl, colors = { nickel: nickelColor, needlePunch: needlePunchColor }) => {
    if (!productObject) return;

    const scene = sceneRef.current;
    if (!scene) return;

    // Get the original product data and position
    const originalData = productObject.userData.originalData;
    const productId = productObject.userData.productId;

    console.log(`Reloading GLB model with URL: ${newGLBUrl}`);

    // Remove the current GLB object from scene
    scene.remove(productObject);

    // Also remove associated pocket objects
    const pocketObjectsToRemove = [];
    scene.children.forEach(child => {
      if (child.userData.isPocket && child.userData.productId === productId) {
        pocketObjectsToRemove.push(child);
      }
    });
    pocketObjectsToRemove.forEach(pocket => {
      // Cancel any ongoing animations
      if (pocket.userData.animationId) {
        cancelAnimationFrame(pocket.userData.animationId);
        pocket.userData.animationId = null;
      }
      
      scene.remove(pocket);
      // Dispose of pocket geometries and materials
      if (pocket.geometry) {
        pocket.geometry.dispose();
      }
      if (pocket.material) {
        pocket.material.dispose();
      }
    });

    // Dispose of geometries and materials
    productObject.traverse((child) => {
      if (child.geometry) {
        child.geometry.dispose();
      }
      if (child.material) {
        if (Array.isArray(child.material)) {
          child.material.forEach(material => material.dispose());
        } else {
          child.material.dispose();
        }
      }
    });

    // Track loading state
    setLoadingGLBModels(prev => new Set(prev).add(productId));

    // Load new GLB model
    const gltfLoader = new GLTFLoader();
    gltfLoader.load(
      newGLBUrl,
      (gltf) => {
        console.log('New GLB model loaded successfully:', gltf);

        const glbObject = gltf.scene;

        // Process the new GLB object
        const processGLBObject = (object, depth = 0, parentIsE45BKT = false) => {
          const indent = '  '.repeat(depth);
          console.log(`${indent}Processing object: ${object.name || 'unnamed'} (type: ${object.type})`);
          
          const isE45BKTObject = object.name && object.name.startsWith('E45-BKT');
          const shouldApplyE45BKTColor = false; // isE45BKTObject || parentIsE45BKT;
          
          if (object.isMesh) {
            object.castShadow = true;
            object.receiveShadow = true;
            
            if (object.material) {
              const materials = Array.isArray(object.material) ? object.material : [object.material];
              
              materials.forEach((material, index) => {
                const clonedMaterial = material.clone();
                
                if (material.name) {
                  clonedMaterial.name = material.name;
                  console.log(`${indent}    Material found: "${material.name}" (Type: ${material.type}) on mesh: ${object.name || 'unnamed'}`);
                }
                
                if (shouldApplyE45BKTColor) {
                  clonedMaterial.color.setHex(0xff0000);
                  console.log(`${indent}    Applied red color to mesh: ${object.name || 'unnamed'} (material ${index}) - Parent: E45-BKT`);
                }
                
                if (clonedMaterial.map) {
                  clonedMaterial.needsUpdate = true;
                }
                
                if (Array.isArray(object.material)) {
                  object.material[index] = clonedMaterial;
                } else {
                  object.material = clonedMaterial;
                }
              });
            }
          }
          
          if (object.children && object.children.length > 0) {
            object.children.forEach(child => {
              processGLBObject(child, depth + 1, shouldApplyE45BKTColor);
            });
          }
        };

        processGLBObject(glbObject);

        // Get canvas dimensions and position for alignment
        const canvasDimensions = getCanvasDimensions();
        const centerX = originalData.x + originalData.width / 2;
        const centerY = originalData.y + originalData.height / 2;
        const normalized = normalize2DTo3D(centerX, centerY, canvasDimensions.width, canvasDimensions.height);

        // Determine wall position and get product configuration
        const allObjects = floorPlanData?.objects || [];
        const wallPosition = determineWallPosition(originalData, allObjects);
        const wallType = originalData.wallType || 'horizontal';
        const productConfig = getProductConfig(originalData, wallType, wallPosition, newGLBUrl);

        // Align the new GLB model
        const { scale, scaledSize } = alignGLBToCeiling(glbObject, normalized, wallHeight, productConfig);

        console.log('New GLB model aligned with scale:', scale);

        // Add user data for identification
        glbObject.userData.isFloorPlanObject = true;
        glbObject.userData.originalData = originalData;
        glbObject.userData.type = 'product';
        glbObject.userData.isGLB = true;
        glbObject.userData.productId = productId;
        // Store current position based on URL
        const positionFromUrl = {
          'https://api.daylighting360.com/bracket4.glb': 'full up',
          'https://api.daylighting360.com/bracket2.glb': 'half way', 
          'https://api.daylighting360.com/bracket3.glb': 'full down'
        };
        glbObject.userData.currentPosition = positionFromUrl[newGLBUrl] || 'full up';

        // Apply the current colors to the new model
        applyMaterialColors(glbObject, colors.nickel, colors.needlePunch);

        // Add to scene
        scene.add(glbObject);

        // Create pocket for this reloaded shade product
        const productPosition = {
          x: glbObject.position.x,
          y: glbObject.position.y,
          z: glbObject.position.z
        };
        createPocket(originalData, wallPosition, productPosition, productConfig);

        // Update the selected product reference if this was the selected product
        if (selectedProduct && selectedProduct.userData.productId === productId) {
          setSelectedProduct(glbObject);
        }

        console.log(`Reloaded GLB product at (${normalized.x}, ${normalized.z}) with scale ${scale}`);

        // Remove from loading state
        setLoadingGLBModels(prev => {
          const newSet = new Set(prev);
          newSet.delete(productId);
          return newSet;
        });
      },
      (progress) => {
        console.log('GLB loading progress:', (progress.loaded / progress.total * 100) + '%');
      },
      (error) => {
        console.error('Error loading new GLB model:', error);
        
        // Remove from loading state
        setLoadingGLBModels(prev => {
          const newSet = new Set(prev);
          newSet.delete(productId);
          return newSet;
        });
      }
    );
  };

  // Handle color and position changes
  const handleApplyChanges = () => {
    if (applyToAll) {
      // Apply to all product objects
      const allProducts = getAllProductObjects();
      console.log(`Applying changes to ${allProducts.length} products`);
      
      allProducts.forEach((productObject) => {
        const currentPosition = productObject.userData.currentPosition || 'full up';
        
        // Check if position changed for this product
        if (selectedPosition !== currentPosition) {
          console.log(`Product ${productObject.userData.productId}: Position changed from ${currentPosition} to ${selectedPosition}, updating fabric`);
          
          // Update fabric scaling instead of reloading GLB
          updateFabricPosition(productObject, selectedPosition);
        }
        
        // Always apply colors
        console.log(`Product ${productObject.userData.productId}: Applying colors`);
        applyMaterialColors(productObject, nickelColor, needlePunchColor);
      });
      
      console.log('Applied changes to all products');
    } else if (selectedProduct) {
      // Apply to selected product only
      const currentPosition = selectedProduct.userData.currentPosition || 'full up';
      
      // Check if position changed
      if (selectedPosition !== currentPosition) {
        console.log(`Position changed from ${currentPosition} to ${selectedPosition}, updating fabric`);
        
        // Update fabric scaling instead of reloading GLB
        updateFabricPosition(selectedProduct, selectedPosition);
      }
      
      // Always apply colors
      console.log('Applying colors to selected product');
      applyMaterialColors(selectedProduct, nickelColor, needlePunchColor);
      
      // Log available materials for debugging
      const availableMaterials = getAvailableMaterials(selectedProduct);
      console.log('Available materials in this product:', Array.from(availableMaterials.values()));
    }
    setIsColorModalOpen(false);
  };

  // Keep the old function for backward compatibility
  const handleApplyColors = () => {
    handleApplyChanges();
  };

  // Helper function to position test cube at first room's top-left corner
  const positionTestCubeToFirstRoom = (objects) => {
    if (!testCubeRef.current) return;
    
    // Find the first room in the objects array
    const firstRoom = objects.find(obj => obj && obj.type === 'room');
    
    if (!firstRoom) {
      console.log('No room found to position test cube');
      return;
    }
    
    console.log('Positioning test cube to first room:', firstRoom);
    
    // Get canvas dimensions for coordinate conversion
    const canvasDimensions = getCanvasDimensions();
    
    // Calculate the top-left corner of the room in 3D space
    // Room coordinates are top-left in 2D, we want the top-left corner
    const topLeftX = firstRoom.x;
    const topLeftY = firstRoom.y;
    
    // Convert to 3D coordinates
    const normalized = normalize2DTo3D(topLeftX, topLeftY, canvasDimensions.width, canvasDimensions.height);
    
    // Position the test cube at the top-left corner of the room
    // Place it slightly above the floor
    testCubeRef.current.position.set(
      normalized.x, 
      0.5, // Half cube height above floor
      normalized.z
    );
    
    console.log(`Test cube positioned at room top-left corner: x=${normalized.x}, z=${normalized.z}`);
  };

  // Helper function to create window/door snap point spheres
  const createWindowDoorSnapSpheres = (objects) => {
    const scene = sceneRef.current;
    if (!scene) return;
    
    const rooms = objects.filter(obj => obj && obj.type === 'room');
    const canvasDimensions = getCanvasDimensions();
    
    console.log('Creating window/door snap spheres for', rooms.length, 'rooms');
    
    rooms.forEach(room => {
      const { x, y, width, height } = room;
      
      const margin = MEASUREMENT_CONSTANTS.PIXELS_PER_FOOT; // 1ft margin from corners
      
      // Helper function to calculate equidistant snap points (same as 2D)
      const calculateEquidistantPoints = (wallLength) => {
        const availableLength = wallLength - (2 * margin); // Subtract margins from both ends
        const maxPoints = Math.floor(availableLength / MEASUREMENT_CONSTANTS.GRID_PIXELS); // Max points that can fit
        
        if (maxPoints <= 0) return []; // Wall too small for any snap points
        
        // Calculate actual spacing to make points equidistant
        const actualSpacing = availableLength / (maxPoints + 1);
        const snapPositions = [];
        
        for (let i = 1; i <= maxPoints; i++) {
          snapPositions.push(margin + (actualSpacing * i));
        }
        
        return snapPositions;
      };
      
      // Top wall snap points (equidistant)
      const topBottomSnapPositions = calculateEquidistantPoints(width);
      topBottomSnapPositions.forEach(position => {
        const snapX = x + position;
        const snapY = y;
        const normalized = normalize2DTo3D(snapX, snapY, canvasDimensions.width, canvasDimensions.height);
        createSnapSphere(normalized.x, wallHeight / 2, normalized.z, 'window');
      });
      
      // Bottom wall snap points (equidistant)
      topBottomSnapPositions.forEach(position => {
        const snapX = x + position;
        const snapY = y + height;
        const normalized = normalize2DTo3D(snapX, snapY, canvasDimensions.width, canvasDimensions.height);
        createSnapSphere(normalized.x, wallHeight / 2, normalized.z, 'window');
      });
      
      // Left and right wall snap points (equidistant)
      const leftRightSnapPositions = calculateEquidistantPoints(height);
      leftRightSnapPositions.forEach(position => {
        const snapX = x;
        const snapY = y + position;
        const normalized = normalize2DTo3D(snapX, snapY, canvasDimensions.width, canvasDimensions.height);
        createSnapSphere(normalized.x, wallHeight / 2, normalized.z, 'window');
      });
      
      leftRightSnapPositions.forEach(position => {
        const snapX = x + width;
        const snapY = y + position;
        const normalized = normalize2DTo3D(snapX, snapY, canvasDimensions.width, canvasDimensions.height);
        createSnapSphere(normalized.x, wallHeight / 2, normalized.z, 'window');
      });
    });
  };

  // Helper function to create product grid snap spheres
  const createProductGridSnapSpheres = (objects) => {
    const scene = sceneRef.current;
    if (!scene) return;
    
    const rooms = objects.filter(obj => obj && obj.type === 'room');
    const canvasDimensions = getCanvasDimensions();
    const gridSize = MEASUREMENT_CONSTANTS.GRID_PIXELS; // 5ft grid for product placement
    
    console.log('Creating product grid snap spheres for', rooms.length, 'rooms');
    
    rooms.forEach(room => {
      const { x, y, width, height } = room;
      const margin = 0; // No margin to allow snap points right at the walls
      
      // Helper function to calculate equidistant grid positions (same as 2D)
      const calculateEquidistantGridPositions = (roomDimension) => {
        const availableLength = roomDimension - (2 * margin); // Subtract margins from both ends
        const maxPoints = Math.floor(availableLength / gridSize); // Max points that can fit
        
        if (maxPoints <= 0) return []; // Room too small for any grid points
        
        // Calculate actual spacing to make points equidistant
        const actualSpacing = availableLength / maxPoints;
        const gridPositions = [];
        
        for (let i = 0; i <= maxPoints; i++) {
          gridPositions.push(margin + (actualSpacing * i));
        }
        
        return gridPositions;
      };
      
      // Calculate equidistant positions for both X (west-east) and Y (north-south) directions
      const xPositions = calculateEquidistantGridPositions(width);
      const yPositions = calculateEquidistantGridPositions(height);
      
      // Generate grid points using equidistant spacing
      xPositions.forEach(relativeX => {
        yPositions.forEach(relativeY => {
          const gridX = x + relativeX;
          const gridY = y + relativeY;
          const normalized = normalize2DTo3D(gridX, gridY, canvasDimensions.width, canvasDimensions.height);
          createSnapSphere(normalized.x, wallHeight - 0.1, normalized.z, 'product');
        });
      });
    });
  };

  // Helper function to create individual snap sphere
  const createSnapSphere = (x, y, z, type) => {
    const scene = sceneRef.current;
    if (!scene) return;
    
    const sphereGeometry = new THREE.SphereGeometry(0.02, 8, 6); // Small sphere
    const sphereMaterial = new THREE.MeshLambertMaterial({ 
      color: type === 'window' ? 0x00ff00 : 0x00aa00, // Bright green for windows, darker green for products
      transparent: true,
      opacity: 0.8
    });
    
    const sphere = new THREE.Mesh(sphereGeometry, sphereMaterial);
    sphere.position.set(x, y, z);
    sphere.userData.isSnapSphere = true;
    sphere.userData.snapType = type;
    
    scene.add(sphere);
  };

  // Helper function to create pocket for shades
  const createPocket = (productData, wallPosition, productPosition, productConfig) => {
    const scene = sceneRef.current;
    if (!scene) return;
    
    console.log('Creating pocket for product:', productData);
    
    const inch_4 = MEASUREMENT_CONSTANTS.SCALE_3D_TO_REAL_INCH * 4 * 2;
    // Pocket dimensions
    const pocketDepth = 0.02; // Small depth for the pocket lines
    const pocketThickness = inch_4; // Very thin lines
    const pocket2Depth = inch_4; // Small depth for the pocket lines
    const pocket2Thickness = 0.02; // Very thin lines
    const pocketLength = MEASUREMENT_CONSTANTS.SCALE_3D_TO_REAL * 5 + (MEASUREMENT_CONSTANTS.SCALE_3D_TO_REAL_INCH * 5); // Length of each pocket line
    const pocketSpacing = 0.1; // Distance between the two pocket lines

    const fabricDepth = MEASUREMENT_CONSTANTS.SCALE_3D_TO_REAL_INCH / 2;
    const fabricThickness = MEASUREMENT_CONSTANTS.SCALE_3D_TO_REAL_INCH * 4;
    const fabricLength = MEASUREMENT_CONSTANTS.SCALE_3D_TO_REAL * 5;
    
    // Pocket material - 50% transparent
    const pocketMaterial = new THREE.MeshLambertMaterial({
      color: 0x888888, // Gray color
      transparent: true,
      opacity: 0.5 // 50% transparency
    });

    const fabricMaterial = new THREE.MeshPhysicalMaterial({
      color: 0x888888, // Gray color
      transparent: true,
      opacity: 0.9, // 50% transparency
      transmission: 0.4, // High light transmission for realistic fabric effect
      // roughness: 0.3, // Slightly rough surface
      // metalness: 0.0 // Non-metallic
    });
    
    // Create two horizontal pocket lines
    const line1Geometry = new THREE.BoxGeometry(pocketLength, pocketThickness, pocketDepth);
    const line2Geometry = new THREE.BoxGeometry(pocketLength, pocket2Thickness, pocket2Depth);
    const fabricGeometry = new THREE.BoxGeometry(fabricLength, fabricThickness, fabricDepth);
    
    const pocketLine1 = new THREE.Mesh(line1Geometry, pocketMaterial);
    const pocketLine2 = new THREE.Mesh(line2Geometry, pocketMaterial);
    const fabric = new THREE.Mesh(fabricGeometry, fabricMaterial);
    // Position the pocket lines based on wall orientation
    let line1Position = { ...productPosition };
    let line2Position = { ...productPosition };
    let fabricPosition = { ...productPosition };
    let rotation = { x: 0, y: 0, z: 0 };
    
    // Adjust positioning based on wall position to place pocket next to the product
    const pocketOffset = 0.09; // Distance from product center to pocket
    const fabricOffset = MEASUREMENT_CONSTANTS.SCALE_3D_TO_REAL_INCH;
    
    switch (wallPosition) {
      case 'north': // Top wall
        // Place pocket lines parallel to the wall (horizontal)
        line1Position.z += pocketOffset;
        line1Position.x -= pocketLength / 2

        line2Position.z += pocketOffset - (inch_4 / 2);
        line2Position.x -= pocketLength / 2

        fabricPosition.z += fabricOffset
        fabricPosition.x -= pocketLength / 2;

        rotation.y = 0; // No rotation needed for horizontal lines
        break;
      case 'south': // Bottom wall  
      
        line1Position.z -= pocketOffset;
        line1Position.x += pocketLength / 2

        line2Position.z -= pocketOffset - (inch_4 / 2);
        line2Position.x += pocketLength / 2

        fabricPosition.z -= fabricOffset
        fabricPosition.x += pocketLength / 2;

        rotation.y = 0;
        break;
      case 'east': // Right wall
        // Place pocket lines parallel to the wall (vertical)
        line1Position.x -= pocketOffset;
        line1Position.z -= pocketLength / 2

        line2Position.x -= pocketOffset - (inch_4 / 2);
        line2Position.z -= pocketLength / 2

        fabricPosition.x -= fabricOffset
        fabricPosition.z -= pocketLength / 2;

        rotation.y = Math.PI / 2; // 90 degree rotation for vertical orientation
        break;
      case 'west': // Left wall
        line1Position.x += pocketOffset;
        line1Position.z += pocketLength / 2

        line2Position.x += pocketOffset - (inch_4 / 2);
        line2Position.z += pocketLength / 2

        fabricPosition.x += fabricOffset
        fabricPosition.z += pocketLength / 2;

        rotation.y = Math.PI / 2;
        break;
      default:
        // Default to north wall positioning
        line1Position.z += pocketOffset;
        line1Position.x -= pocketLength / 2

        line2Position.z -= pocketOffset - (inch_4 / 2);
        line2Position.x -= pocketLength / 2

        fabricPosition.x += fabricOffset
        fabricPosition.z -= pocketLength / 2;

        rotation.y = 0; // No rotation needed for horizontal lines
        break;
    }

    line1Position.y -= 0.035;
    fabricPosition.y -= 0.035 + (MEASUREMENT_CONSTANTS.SCALE_3D_TO_REAL_INCH * 2);

    pocketLine1.position.set(line1Position.x, line1Position.y, line1Position.z);
    pocketLine1.rotation.set(rotation.x, rotation.y, rotation.z);
    pocketLine1.castShadow = true;
    pocketLine1.receiveShadow = true;
    pocketLine1.userData.isFloorPlanObject = true;
    pocketLine1.userData.originalData = productData;
    pocketLine1.userData.type = 'pocket';
    pocketLine1.userData.isPocket = true;
    pocketLine1.userData.productId = productData.id || `product_${Date.now()}`;
        
    pocketLine2.position.set(line2Position.x, line2Position.y, line2Position.z);
    pocketLine2.rotation.set(rotation.x, rotation.y, rotation.z);  
    pocketLine2.castShadow = true;
    pocketLine2.receiveShadow = true;
    pocketLine2.userData.isFloorPlanObject = true;
    pocketLine2.userData.originalData = productData;
    pocketLine2.userData.type = 'pocket';
    pocketLine2.userData.isPocket = true;
    pocketLine2.userData.productId = productData.id || `product_${Date.now()}`;

    fabric.position.set(fabricPosition.x, fabricPosition.y, fabricPosition.z);
    fabric.rotation.set(rotation.x, rotation.y, rotation.z);
    fabric.userData.isFloorPlanObject = true;
    fabric.userData.originalData = productData;
    fabric.userData.type = 'pocket';
    fabric.userData.isPocket = true;
    fabric.userData.productId = productData.id || `product_${Date.now()}`;
    fabric.castShadow = true;
    fabric.receiveShadow = true;
    
    // Add to scene
    scene.add(pocketLine1);
    scene.add(pocketLine2);
    scene.add(fabric);
    
    console.log(`Created pocket for product at wall position: ${wallPosition}`);
    console.log(`Pocket line 1 position: (${line1Position.x}, ${line1Position.y}, ${line1Position.z})`);
    console.log(`Pocket line 2 position: (${line2Position.x}, ${line2Position.y}, ${line2Position.z})`);
    
    return { pocketLine1, pocketLine2, fabric };
  };

  // Initialize Three.js scene
  useEffect(() => {
    if (!mountRef.current || isInitialized) return;

    const dimensions = getContainerDimensions();

    // Scene
    const scene = new THREE.Scene();
    scene.background = new THREE.Color(0xf0f0f0);
    sceneRef.current = scene;

    // Camera
    const camera = new THREE.PerspectiveCamera(
      75,
      dimensions.width / dimensions.height,
      0.1,
      1000
    );
    // Start with reflective ceiling plan view (from below looking up)
    camera.position.set(0, -5, 0);
    camera.lookAt(0, wallHeight, 0);
    cameraRef.current = camera;

    // Renderer
    const renderer = new THREE.WebGLRenderer({ antialias: true });
    renderer.setSize(dimensions.width, dimensions.height);
    renderer.shadowMap.enabled = true;
    renderer.shadowMap.type = THREE.PCFSoftShadowMap;
    mountRef.current.appendChild(renderer.domElement);
    rendererRef.current = renderer;

    // Controls
    const controls = new OrbitControls(camera, renderer.domElement);
    controls.enableDamping = true;
    controls.dampingFactor = 0.05;
    controls.screenSpacePanning = false;
    controls.minDistance = 1;
    controls.maxDistance = 100;
    // Remove polar angle limit entirely to allow camera below floor level for ceiling plan view
    // controls.maxPolarAngle = Math.PI / 2; // Commented out to allow full rotation
    // Set initial target to ceiling level for reflective ceiling plan view
    controls.target.set(0, wallHeight, 0);
    controls.update();
    controlsRef.current = controls;

    // Lighting
    const ambientLight = new THREE.AmbientLight(0x404040, 0.6);
    scene.add(ambientLight);

    const directionalLight = new THREE.DirectionalLight(0xffffff, 0.8);
    directionalLight.position.set(10, 10, 5);
    directionalLight.castShadow = true;
    directionalLight.shadow.mapSize.width = 2048;
    directionalLight.shadow.mapSize.height = 2048;
    scene.add(directionalLight);

    // Additional lighting from below for ceiling plan view
    const bottomLight = new THREE.DirectionalLight(0xffffff, 0.4);
    bottomLight.position.set(0, -10, 0); // Light from below
    bottomLight.target.position.set(0, wallHeight, 0); // Target the ceiling
    scene.add(bottomLight);
    scene.add(bottomLight.target);

    // Point lights for better ceiling illumination
    const pointLight1 = new THREE.PointLight(0xffffff, 0.3, 20);
    pointLight1.position.set(-5, wallHeight + 1, -5);
    scene.add(pointLight1);

    const pointLight2 = new THREE.PointLight(0xffffff, 0.3, 20);
    pointLight2.position.set(5, wallHeight + 1, 5);
    scene.add(pointLight2);

    // Grid helper (keep the floor grid)
    const gridHelper = new THREE.GridHelper(200, 200, 0x888888, 0xcccccc);
    scene.add(gridHelper);

    // Floor
    const floorGeometry = new THREE.PlaneGeometry(200, 200);
    const floorMaterial = new THREE.MeshLambertMaterial({ 
      color: 0xffffff,
      transparent: true,
      opacity: 0.8
    });
    const floor = new THREE.Mesh(floorGeometry, floorMaterial);
    floor.rotation.x = -Math.PI / 2;
    floor.receiveShadow = true;
    scene.add(floor);

    // Add a test cube to verify 3D rendering is working
    const testGeometry = new THREE.BoxGeometry(1, 1, 1);
    const testMaterial = new THREE.MeshLambertMaterial({ color: 0xff0000 });
    const testCube = new THREE.Mesh(testGeometry, testMaterial);
    testCube.position.set(0, 1, 0);
    testCube.castShadow = true;
    testCube.receiveShadow = true;
    testCube.userData.isTestCube = true; // Mark as test cube
    // scene.add(testCube);
    testCubeRef.current = testCube; // Store reference for later positioning

    // setIsInitialized(true);
    loadData();

    // Animation loop
    const animate = () => {
      requestAnimationFrame(animate);
      controls.update();
      renderer.render(scene, camera);
    };
    animate();

    // Handle resize
    const handleResize = () => {
      if (!mountRef.current) return;
      
      const newDimensions = getContainerDimensions();
      
      camera.aspect = newDimensions.width / newDimensions.height;


      camera.updateProjectionMatrix();
      renderer.setSize(newDimensions.width, newDimensions.height);
    };

    // Add event listeners
    window.addEventListener('resize', handleResize);
    renderer.domElement.addEventListener('click', handleMouseClick);

    return () => {
      window.removeEventListener('resize', handleResize);
      if (renderer.domElement) {
        renderer.domElement.removeEventListener('click', handleMouseClick);
      }
      
      // Clean up GLB objects, snap spheres, pocket objects, and dispose of geometries/materials
      if (scene) {
        scene.traverse((child) => {
          if (child.userData.isFloorPlanObject || child.userData.isSnapSphere || child.userData.isPocket) {
            // Cancel any ongoing animations
            if (child.userData.animationId) {
              cancelAnimationFrame(child.userData.animationId);
              child.userData.animationId = null;
            }
            
            if (child.geometry) {
              child.geometry.dispose();
            }
            if (child.material) {
              if (Array.isArray(child.material)) {
                child.material.forEach(material => material.dispose());
              } else {
                child.material.dispose();
              }
            }
          }
        });
      }
      
      if (mountRef.current && renderer.domElement) {
        mountRef.current.removeChild(renderer.domElement);
      }
      renderer.dispose();
    };
  }, [isInitialized]);

  useEffect(() => {
    if (isInitialized && floorPlanData) {
      loadData();
    }
  }, [floorPlanData, isInitialized]);

  const loadData = () => {
    console.log('Updating 3D scene with floor plan data:', floorPlanData);

    // Clear existing 3D objects (except lights, grid, floor, test cube)
    const scene = sceneRef.current;
    const objectsToRemove = [];
    scene.children.forEach(child => {
      if (child.userData.isFloorPlanObject || child.userData.isSnapSphere || child.userData.isPocket) {
        objectsToRemove.push(child);
      }
    });
    objectsToRemove.forEach(obj => {
      // Cancel any ongoing animations
      if (obj.userData.animationId) {
        cancelAnimationFrame(obj.userData.animationId);
        obj.userData.animationId = null;
      }
      
      scene.remove(obj);
      // Dispose of geometries and materials for pocket objects too
      if (obj.userData.isPocket) {
        if (obj.geometry) {
          obj.geometry.dispose();
        }
        if (obj.material) {
          obj.material.dispose();
        }
      }
    });

    // Defensive: If no floorPlanData or no objects, skip
    if (!floorPlanData) {
      console.log('No floor plan data provided');
      return;
    }

    // Handle different data structures
    let objects = [];
    if (Array.isArray(floorPlanData)) {
      objects = floorPlanData;
    } else if (floorPlanData.objects && Array.isArray(floorPlanData.objects)) {
      objects = floorPlanData.objects;
    } else if (typeof floorPlanData === 'object') {
      // Try to extract objects from various possible structures
      objects = floorPlanData.objects || floorPlanData.data?.objects || [];
    }

    if (objects.length === 0) {
      console.log('No objects found in floor plan data:', floorPlanData);
      return;
    }

    console.log('Processing', objects.length, 'objects for 3D rendering');

    // Process objects in specific order to ensure windows are created before rooms
    // This allows walls to have holes cut out for windows
    
    // First pass: Process windows and doors (they need to be available when creating rooms)
    console.log('First pass: Processing windows and doors');
    objects.forEach((obj, index) => {
      if (!obj || !obj.type) {
        console.log('Skipping invalid object:', obj);
        return;
      }
      
      switch (obj.type) {
        case 'window':
          console.log(`Processing window ${index}:`, obj);
          createWindow(obj, objects);
          break;
        case 'door':
          console.log(`Processing door ${index}:`, obj);
          createDoor(obj);
          break;
      }
    });
    
    // Second pass: Process rooms (now windows are available for wall holes)
    console.log('Second pass: Processing rooms');
    objects.forEach((obj, index) => {
      if (!obj || !obj.type) return;
      
      if (obj.type === 'room') {
        console.log(`Processing room ${index}:`, obj);
        createRoom(obj, objects);
      }
    });
    
    // Third pass: Process remaining objects
    console.log('Third pass: Processing products and walls');
    objects.forEach((obj, index) => {
      if (!obj || !obj.type) return;
      
      switch (obj.type) {
        case 'product':
          console.log(`Processing product ${index}:`, obj);
          createProduct(obj, objects);
          break;
        case 'wall':
          console.log(`Processing wall ${index}:`, obj);
          createWall(obj);
          break;
        default:
          // Skip already processed types (room, window, door)
          if (!['room', 'window', 'door'].includes(obj.type)) {
            console.log('Unknown object type:', obj.type);
          }
      }
    });
    
    // Position test cube at the top-left corner of the first room
    positionTestCubeToFirstRoom(objects);
    
    // Create snap point spheres for visual reference
    createWindowDoorSnapSpheres(objects);
    createProductGridSnapSpheres(objects);
  }

  const createRoom = (roomData, allObjects = []) => {
    const scene = sceneRef.current;
    
    console.log('Creating room with data:', roomData);
    console.log(`Wall height: ${wallHeight} 3D units (${units3DToFeet(wallHeight)} feet)`);
    
    // Get canvas dimensions for proper coordinate normalization
    const canvasDimensions = getCanvasDimensions();
    
    // Convert coordinates using proper normalization
    // Convert from pixels to feet, then scale for 3D scene
    const widthInFeet = roomData.width / MEASUREMENT_CONSTANTS.PIXELS_PER_FOOT;
    const heightInFeet = roomData.height / MEASUREMENT_CONSTANTS.PIXELS_PER_FOOT;
    const width = widthInFeet * MEASUREMENT_CONSTANTS.SCALE_3D_TO_REAL;
    const height = heightInFeet * MEASUREMENT_CONSTANTS.SCALE_3D_TO_REAL;
    
    const centerX = roomData.x + roomData.width / 2;
    const centerY = roomData.y + roomData.height / 2;
    const normalized = normalize2DTo3D(centerX, centerY, canvasDimensions.width, canvasDimensions.height);
    
    console.log(`Room coordinates: x=${normalized.x}, z=${normalized.z}, width=${width}, height=${height}`);
    
    // Create 4 walls for the room
    const wallThickness = 0.05; // Reduced from 0.2 to 0.1 (50% thinner)
    
    // Wall materials - different colors for horizontal vs vertical walls
    const horizontalMaterial = new THREE.MeshLambertMaterial({ 
      color: 0x888888, // Grey for horizontal walls (north/south)
      transparent: true,
      opacity: 0.8
    });
    
    const verticalMaterial = new THREE.MeshLambertMaterial({ 
      color: 0xffffff, // White for vertical walls (east/west)
      transparent: true,
      opacity: 0.8
    });
    
    // Helper function to create wall with window holes
    const createWallWithHoles = (wallWidth, wallHeight, wallThickness, windowsOnWall, wallPosition) => {
      console.log('Creating wall with holes:', windowsOnWall, 'Wall position:', wallPosition); 
      // Create the main wall shape
      const wallShape = new THREE.Shape();
      wallShape.moveTo(-wallWidth/2, -wallHeight/2);
      wallShape.lineTo(wallWidth/2, -wallHeight/2);
      wallShape.lineTo(wallWidth/2, wallHeight/2);
      wallShape.lineTo(-wallWidth/2, wallHeight/2);
      wallShape.lineTo(-wallWidth/2, -wallHeight/2);

      // Add holes for windows
      if (windowsOnWall && windowsOnWall.length > 0) {
        console.log(`Adding ${windowsOnWall.length} window holes to ${wallPosition} wall`);
        console.log(`Adding window holes to wall`, windowsOnWall);
        windowsOnWall.forEach(window => {
          // Convert window dimensions to 3D world scale
          const windowWidthInFeet = (window.width || 100) / MEASUREMENT_CONSTANTS.PIXELS_PER_FOOT;
          const windowHeightInFeet = (window.height || 150) / MEASUREMENT_CONSTANTS.PIXELS_PER_FOOT;
          let holeWidth = windowWidthInFeet;
          let holeHeight = windowHeightInFeet;
          
          // For east/west walls, the geometry is rotated, so we need to swap dimensions
          // East/West walls are created with room height as width, then rotated 90 degrees
          if (wallPosition === 'north' || wallPosition === 'south') {
            
            holeWidth = windowWidthInFeet * MEASUREMENT_CONSTANTS.SCALE_3D_TO_REAL * 2;
            holeHeight = windowHeightInFeet - (MEASUREMENT_CONSTANTS.SCALE_3D_TO_REAL * 1.5);
            // Swap dimensions because the wall will be rotated
            [holeWidth, holeHeight] = [holeHeight, holeWidth];
            console.log(`Swapped dimensions for ${wallPosition} wall: ${holeWidth} x ${holeHeight}`);
          }
          else {
            holeWidth = windowWidthInFeet - (MEASUREMENT_CONSTANTS.SCALE_3D_TO_REAL * 1.5);
            holeHeight = windowHeightInFeet * MEASUREMENT_CONSTANTS.SCALE_3D_TO_REAL * 2;
          }
          
          console.log(`Creating window hole for ${wallPosition} wall: ${holeWidth} x ${holeHeight}`);
          
          // Create window hole (centered on wall for now - can be adjusted for positioning)
          const holeShape = new THREE.Shape();
          holeShape.moveTo(-holeWidth/2, -holeHeight/2);
          holeShape.lineTo(holeWidth/2, -holeHeight/2);
          holeShape.lineTo(holeWidth/2, holeHeight/2);
          holeShape.lineTo(-holeWidth/2, holeHeight/2);
          holeShape.lineTo(-holeWidth/2, -holeHeight/2);
          
          // Add hole to wall shape
          wallShape.holes.push(holeShape);
        });
      }

      // Extrude the shape to create 3D geometry
      const extrudeSettings = {
        depth: wallThickness,
        bevelEnabled: false
      };
      
      return new THREE.ExtrudeGeometry(wallShape, extrudeSettings);
    };

    // Helper function to get windows for a specific wall
    const getWindowsForWall = (wallPosition, roomData, allObjects) => {
      
      if (!allObjects || !Array.isArray(allObjects)) return [];
      
      // Filter windows that belong to this wall type
      const roomWindows = allObjects.filter(obj => 
        obj.type === 'window' && 
        obj.wallPosition === wallPosition
      );
      
      console.log(`Found ${roomWindows.length} windows for ${wallPosition} wall`);
      return roomWindows;
    };

    // Create 4 walls with window holes
    const walls = [];
    
    // Get windows for each wall
    const northWindows = getWindowsForWall('north', roomData, allObjects);
    const southWindows = getWindowsForWall('south', roomData, allObjects);
    const eastWindows = getWindowsForWall('east', roomData, allObjects);
    const westWindows = getWindowsForWall('west', roomData, allObjects);
    
    // North wall (top) - horizontal
    const northWallGeometry = createWallWithHoles(width, wallHeight, wallThickness, northWindows, 'north');
    const northWall = new THREE.Mesh(northWallGeometry, horizontalMaterial);
    northWall.position.set(normalized.x, wallHeight/2, normalized.z - height/2);
    northWall.castShadow = true;
    northWall.userData.isFloorPlanObject = true;
    northWall.userData.originalData = roomData;
    northWall.userData.wallType = 'north';
    scene.add(northWall);
    walls.push(northWall);
    
    // South wall (bottom) - horizontal
    const southWallGeometry = createWallWithHoles(width, wallHeight, wallThickness, southWindows, 'south');
    const southWall = new THREE.Mesh(southWallGeometry, horizontalMaterial);
    southWall.position.set(normalized.x, wallHeight/2, normalized.z + height/2);
    southWall.castShadow = true;
    southWall.userData.isFloorPlanObject = true;
    southWall.userData.originalData = roomData;
    southWall.userData.wallType = 'south';
    scene.add(southWall);
    walls.push(southWall);
    
    // East wall (right) - vertical
    const eastWallGeometry = createWallWithHoles(height, wallHeight, wallThickness, eastWindows, 'east');
    const eastWall = new THREE.Mesh(eastWallGeometry, verticalMaterial);
    eastWall.position.set(normalized.x + width/2, wallHeight/2, normalized.z);
    eastWall.rotation.y = Math.PI/2; // Rotate for vertical wall
    eastWall.castShadow = true;
    eastWall.userData.isFloorPlanObject = true;
    eastWall.userData.originalData = roomData;
    eastWall.userData.wallType = 'east';
    scene.add(eastWall);
    walls.push(eastWall);
    
    // West wall (left) - vertical
    const westWallGeometry = createWallWithHoles(height, wallHeight, wallThickness, westWindows, 'west');
    const westWall = new THREE.Mesh(westWallGeometry, verticalMaterial);
    westWall.position.set(normalized.x - width/2, wallHeight/2, normalized.z);
    westWall.rotation.y = Math.PI/2; // Rotate for vertical wall
    westWall.castShadow = true;
    westWall.userData.isFloorPlanObject = true;
    westWall.userData.originalData = roomData;
    westWall.userData.wallType = 'west';
    scene.add(westWall);
    walls.push(westWall);
    
    // Create invisible roof covering the same area as the room
    const roofGeometry = new THREE.PlaneGeometry(width, height);
    const roofMaterial = new THREE.MeshLambertMaterial({
      color: 0xffffff,
      transparent: true,
      opacity: 0.0, // Fully transparent (invisible)
      side: THREE.DoubleSide // Visible from both sides
    });
    
    const roof = new THREE.Mesh(roofGeometry, roofMaterial);
    roof.position.set(normalized.x, wallHeight, normalized.z); // Position at top of walls
    roof.rotation.x = -Math.PI / 2; // Rotate to be horizontal (roof-like)
    roof.receiveShadow = true; // Can receive shadows from objects above
    roof.castShadow = true; // Cast shadows to block light from above
    roof.userData.isFloorPlanObject = true;
    roof.userData.originalData = roomData;
    roof.userData.type = 'roof';
    scene.add(roof);
    
    console.log(`Created invisible roof at height ${wallHeight} for room at (${normalized.x}, ${normalized.z})`);
    console.log(`Created 4 walls with window holes for room at (${normalized.x}, ${normalized.z}) with dimensions ${width}x${height}`);
  };

  const createWindow = (windowData, allObjects = []) => {
    const scene = sceneRef.current;
    
    console.log('Creating window with data:', windowData);
    console.log('Window wallType:', windowData.wallType || 'horizontal');
    
    // Determine wall position for consistent alignment with products
    const wallPosition = determineWallPosition(windowData, allObjects);
    const wallType = windowData.wallType || 'horizontal';

    windowData.wallPosition = wallPosition;
    
    console.log('Window wall position:', wallPosition);
    console.log('Window wall type:', wallType);
    
    // Get canvas dimensions for proper coordinate normalization
    const canvasDimensions = getCanvasDimensions();
    
    // Position (convert from Konva coordinates using proper normalization)
    const centerX = windowData.x + windowData.width / 2;
    const centerY = windowData.y + windowData.height / 2;
    const normalized = normalize2DTo3D(centerX, centerY, canvasDimensions.width, canvasDimensions.height);
    
    console.log(`Window coordinates: x=${normalized.x}, z=${normalized.z}`);
    
    // Set the GLB URL for windows
    const glbUrl = "https://api.daylighting360.com/window5x10.glb";
    const windowId = windowData.id || `window_${Date.now()}`;
    
    console.log('Loading GLB window model from URL:', glbUrl);
    
    // Track loading state
    setLoadingGLBModels(prev => new Set(prev).add(windowId));
    
    // Create a loading placeholder (small cube) while GLB loads
    const placeholderGeometry = new THREE.BoxGeometry(0.1, 0.1, 0.1);
    const placeholderMaterial = new THREE.MeshLambertMaterial({ 
      color: 0x87ceeb,
      transparent: true,
      opacity: 0.5
    });
    const placeholder = new THREE.Mesh(placeholderGeometry, placeholderMaterial);
    placeholder.position.set(normalized.x, wallHeight, normalized.z);
    placeholder.userData.isFloorPlanObject = true;
    placeholder.userData.originalData = windowData;
    placeholder.userData.type = 'window';
    placeholder.userData.isPlaceholder = true;
    placeholder.userData.windowId = windowId;
    scene.add(placeholder);
    
    // Load GLB model
    const gltfLoader = new GLTFLoader();
    gltfLoader.load(
      glbUrl,
      (gltf) => {
        console.log('GLB window model loaded successfully:', gltf);
        
        // Remove placeholder
        scene.remove(placeholder);
        
        const glbObject = gltf.scene;
        
        // Process the GLB object
        glbObject.traverse((child) => {
          if (child.isMesh) {
            // Enable shadows for all meshes
            // child.castShadow = true;
            // child.receiveShadow = true;
            
            // Apply materials if they exist
            if (child.material) {
              // Ensure materials are properly configured for lighting
              if (child.material.map) {
                child.material.needsUpdate = true;
              }
            }
          }
        });
        
        // Get window configuration with proper rotations
        const wallType = windowData.wallType || 'horizontal';
        let windowRotation;
        
        if (wallType === 'vertical') {
          // For vertical walls: 90° around Y-axis only
          windowRotation = { x: 0, y: 0, z: 0 };
          console.log('Applying vertical wall rotation to window');
        } else {
          // For horizontal walls: no rotation needed
          windowRotation = { x: 0, y: Math.PI / 2, z: 0 };
          console.log('Applying horizontal wall rotation to window');
        }
        
        // Get wall offset configuration for alignment with products
        const wallOffsetDistance = 0.025; // Same offset distance as products
        let wallOffset = { x: 0, y: 0, z: 0 };
        let rotation = { x: 0, y: 0, z: 0 };
        
        if (wallType && wallPosition) {
          switch (wallPosition) {
            case 'north': // Top wall
              wallOffset.z = -wallOffsetDistance;
              // windowRotation.x = Math.PI;
              break;
            case 'south': // Bottom wall  
              wallOffset.z = -wallOffsetDistance;
              break;
            case 'east': // Right wall
              wallOffset.x = wallOffsetDistance; 
              break;
            case 'west': // Left wall
              wallOffset.x = wallOffsetDistance;
              break;
          }
        }
        
        const windowConfig = {
          maxScale: 1,
          ceilingOffset: - (wallHeight /2), // Position higher on the wall (30% from top)
          rotation: windowRotation,
          scaleMultiplier: 1,
          wallOffset: wallOffset,
          wallPosition
        };
        
        // Use helper function to align GLB model
        const { scale, scaledSize } = alignGLBToCeiling(glbObject, normalized, wallHeight, windowConfig);
        
        console.log('GLB window model aligned with scale:', scale);
        console.log('GLB window model scaled size:', scaledSize);
        
        // Add user data for identification
        glbObject.userData.isFloorPlanObject = true;
        glbObject.userData.originalData = windowData;
        glbObject.userData.type = 'window';
        glbObject.userData.isGLB = true;
        glbObject.userData.windowId = windowId;
        
        scene.add(glbObject);
        
        console.log(`Created GLB window at (${normalized.x}, ${normalized.z}) with scale ${scale}`);
        
        // Remove from loading state
        setLoadingGLBModels(prev => {
          const newSet = new Set(prev);
          newSet.delete(windowId);
          return newSet;
        });
        

      },
      (progress) => {
        console.log('GLB loading progress:', (progress.loaded / progress.total * 100) + '%');
      },
      (error) => {
        console.error('Error loading GLB window model:', error);
        
        // Remove placeholder on error
        scene.remove(placeholder);
                
        // Remove from loading state
        setLoadingGLBModels(prev => {
          const newSet = new Set(prev);
          newSet.delete(windowId);
          return newSet;
        });
        

      }
    );
  };

  const createDoor = (doorData) => {
    const scene = sceneRef.current;
    
    console.log('Creating door with data:', doorData);
    
    // Get canvas dimensions for proper coordinate normalization
    const canvasDimensions = getCanvasDimensions();
    
    // Door dimensions - handle inverted dimensions for vertical walls
    const wallType = doorData.wallType || 'horizontal';
    let doorWidth;
    const doorDepth = 0.3; // Thicker than walls (wall thickness is 0.1)
    
    if (wallType === 'vertical') {
      // For vertical walls, check if dimensions are inverted
      // Correct vertical door should be narrow (width) and tall (height)
      const widthInFeet = doorData.height / MEASUREMENT_CONSTANTS.PIXELS_PER_FOOT;
      doorWidth = widthInFeet * MEASUREMENT_CONSTANTS.SCALE_3D_TO_REAL;
    } else {
      // For horizontal walls, check if dimensions are inverted
      // Correct horizontal door should be wide (width) and short (height)
      const widthInFeet = doorData.width / MEASUREMENT_CONSTANTS.PIXELS_PER_FOOT;
      doorWidth = widthInFeet * MEASUREMENT_CONSTANTS.SCALE_3D_TO_REAL;
    }
    
    // Position (convert from Konva coordinates using proper normalization)
    const centerX = doorData.x + doorData.width / 2;
    const centerY = doorData.y + doorData.height / 2;
    const normalized = normalize2DTo3D(centerX, centerY, canvasDimensions.width, canvasDimensions.height);
    
    // Door positioning: start from floor, go up to near ceiling
    const doorHeight = wallHeight * 0.8; // 80% of wall height for doors
    const y = doorHeight / 2; // Position from floor (y=0) up to door height
    
    console.log(`Door coordinates: x=${normalized.x}, z=${normalized.z}, width=${doorWidth}, height=${doorHeight}, wallType=${wallType}`);
    
    // Door material (wood-like)
    const material = new THREE.MeshLambertMaterial({ 
      color: 0x8b4513, // Brown wood
      transparent: true,
      opacity: 0.9
    });
    
    // Create door frame
    const frameMaterial = new THREE.MeshLambertMaterial({ 
      color: 0x654321, // Darker brown frame
      transparent: true,
      opacity: 0.9
    });
    
    // Door frame (thicker border)
    const frameThickness = 0.05;
    const frameGeometry = new THREE.BoxGeometry(
      doorWidth + frameThickness * 2, 
      doorHeight + frameThickness * 2, 
      doorDepth + frameThickness
    );
    const frame = new THREE.Mesh(frameGeometry, frameMaterial);
    frame.position.set(normalized.x, y, normalized.z);
    if (wallType === 'vertical') {
      // rotate on Y axis 90 degrees
      frame.rotation.y = Math.PI / 2;
    }
    frame.castShadow = true;
    frame.receiveShadow = true;
    frame.userData.isFloorPlanObject = true;
    frame.userData.originalData = doorData;
    frame.userData.type = 'door';
    scene.add(frame);
    
    // Door panel (inner part)
    const doorGeometry = new THREE.BoxGeometry(doorWidth, doorHeight, doorDepth);
    const door = new THREE.Mesh(doorGeometry, material);
    door.position.set(normalized.x, y, normalized.z);
    if (wallType === 'vertical') {
      // rotate on Y axis 90 degrees
      door.rotation.y = Math.PI / 2;
    }
    door.castShadow = true;
    door.receiveShadow = true;
    door.userData.isFloorPlanObject = true;
    door.userData.originalData = doorData;
    door.userData.type = 'door';
    scene.add(door);
    
    console.log(`Created door at (${normalized.x}, ${normalized.z}) with dimensions ${doorWidth}x${doorHeight}`);
  };

  const createProduct = (productData, allObjects = []) => {
    const scene = sceneRef.current;
    
    console.log('Creating product with data:', productData);
    
    // Determine wall position and type
    const wallPosition = determineWallPosition(productData, allObjects);
    const wallType = productData.wallType || 'horizontal';
    
    console.log('Product wall position:', wallPosition);
    console.log('Product wall type:', wallType);
    
    // Get canvas dimensions for proper coordinate normalization
    const canvasDimensions = getCanvasDimensions();
    
    const centerX = productData.x + productData.width / 2;
    const centerY = productData.y + productData.height / 2;
    const normalized = normalize2DTo3D(centerX, centerY, canvasDimensions.width, canvasDimensions.height);
    
    console.log(`Product coordinates: x=${normalized.x}, z=${normalized.z}`);
    
    // Set the GLB URL for products
    const glbUrl = "https://api.daylighting360.com/bracket4.glb";
    const productId = productData.id || `product_${Date.now()}`;
    
    console.log('Loading GLB product model from URL:', glbUrl);
    
    // Track loading state
    setLoadingGLBModels(prev => new Set(prev).add(productId));
    
    // Get product configuration with wall offset
    const productConfig = getProductConfig(productData, wallType, wallPosition);
    
    // Create a loading placeholder (small cube) while GLB loads
    const placeholderGeometry = new THREE.BoxGeometry(0.1, 0.1, 0.1);
    const placeholderMaterial = new THREE.MeshLambertMaterial({ 
      color: 0x888888,
      transparent: true,
      opacity: 0.5
    });
    const placeholder = new THREE.Mesh(placeholderGeometry, placeholderMaterial);
    
    // Apply wall offset to placeholder as well
    const offsetX = normalized.x + (productConfig.wallOffset?.x || 0);
    const offsetZ = normalized.z + (productConfig.wallOffset?.z || 0);
    placeholder.position.set(offsetX, wallHeight - 0.05, offsetZ);
    
    placeholder.userData.isFloorPlanObject = true;
    placeholder.userData.originalData = productData;
    placeholder.userData.type = 'product';
    placeholder.userData.isPlaceholder = true;
    placeholder.userData.productId = productId;
    scene.add(placeholder);
    
    // Load GLB model
    const gltfLoader = new GLTFLoader();
    gltfLoader.load(
      glbUrl,
      (gltf) => {
        console.log('GLB product model loaded successfully:', gltf);
        
        // Remove placeholder
        scene.remove(placeholder);
        
        const glbObject = gltf.scene;
        
                 // Recursive function to process all nested children
         const processGLBObject = (object, depth = 0, parentIsE45BKT = false) => {
           const indent = '  '.repeat(depth);
           console.log(`${indent}Processing object: ${object.name || 'unnamed'} (type: ${object.type})`);
           
           // Check if this object or its parent is an E45-BKT object
           const isE45BKTObject = object.name && object.name.startsWith('E45-BKT');
           const shouldApplyE45BKTColor = false// isE45BKTObject || parentIsE45BKT;
           
           if (isE45BKTObject) {
             console.log(`${indent}  Found E45-BKT object: ${object.name} (${object.type})`);
             
           }
           
           if (object.isMesh) {
             console.log(`${indent}  Is mesh: ${object.name || 'unnamed'}, shouldApplyE45BKTColor: ${shouldApplyE45BKTColor}`);
             
             // Enable shadows for all meshes
             object.castShadow = true;
             object.receiveShadow = true;
             
             // Apply materials if they exist
             if (object.material) {
               // Handle both single material and material arrays
               const materials = Array.isArray(object.material) ? object.material : [object.material];
               
                               materials.forEach((material, index) => {
                 // Clone material to avoid affecting other instances
                 const clonedMaterial = material.clone();
                 
                 // Store the original material name for color changing
                 if (material.name) {
                   clonedMaterial.name = material.name;
                   console.log(`${indent}    Material found: "${material.name}" (Type: ${material.type}) on mesh: ${object.name || 'unnamed'}`);
                   
                   // Log material properties for debugging
                   const materialInfo = {
                     name: material.name,
                     type: material.type,
                     hasColor: !!material.color,
                     hasMap: !!material.map,
                     hasAlbedoMap: !!material.map, // In Three.js, 'map' is the albedo/diffuse map
                     hasRoughnessMap: !!material.roughnessMap,
                     hasMetalnessMap: !!material.metalnessMap,
                     color: material.color ? `#${material.color.getHexString()}` : 'none'
                   };
                   console.log(`${indent}      Properties:`, materialInfo);
                 }
                 
                 // Apply red color to meshes within E45-BKT objects
                 if (shouldApplyE45BKTColor) {
                   clonedMaterial.color.setHex(0xff0000); // Red color
                   console.log(`${indent}    Applied red color to mesh: ${object.name || 'unnamed'} (material ${index}) - Parent: E45-BKT`);
                 }
                 
                 // Ensure materials are properly configured for lighting
                 if (clonedMaterial.map) {
                   clonedMaterial.needsUpdate = true;
                 }
                 
                 // Update the material reference
                 if (Array.isArray(object.material)) {
                   object.material[index] = clonedMaterial;
                 } else {
                   object.material = clonedMaterial;
                 }
               });
             }
           }
           
           // Recursively process all children, passing down the E45-BKT flag
           if (object.children && object.children.length > 0) {
             console.log(`${indent}  Processing ${object.children.length} children of ${object.name || 'unnamed'}`);
             object.children.forEach(child => {
               processGLBObject(child, depth + 1, shouldApplyE45BKTColor);
             });
           }
         };
         
         // Add GLB structure inspector
         const inspectGLBStructure = (object, depth = 0) => {
           const indent = '  '.repeat(depth);
           const info = {
             name: object.name || 'unnamed',
             type: object.type,
             isMesh: object.isMesh,
             hasMaterial: !!object.material,
             materialCount: object.material ? (Array.isArray(object.material) ? object.material.length : 1) : 0,
             childrenCount: object.children ? object.children.length : 0,
             position: object.position ? `(${object.position.x.toFixed(2)}, ${object.position.y.toFixed(2)}, ${object.position.z.toFixed(2)})` : 'none'
           };
           
           console.log(`${indent}📦 ${info.name} [${info.type}] - Mesh:${info.isMesh} Materials:${info.materialCount} Children:${info.childrenCount} Pos:${info.position}`);
           
           if (object.children && object.children.length > 0) {
             object.children.forEach(child => inspectGLBStructure(child, depth + 1));
           }
         };
         
         console.log('\n🔍 GLB Structure Inspector:');
         inspectGLBStructure(glbObject);
         console.log('\n');

         // Process the GLB object recursively
         processGLBObject(glbObject);
        
        // Use helper function to align GLB model consistently (productConfig already calculated above)
        const { scale, scaledSize } = alignGLBToCeiling(glbObject, normalized, wallHeight, productConfig);
        
        console.log('GLB product model aligned with scale:', scale);
        console.log('GLB product model scaled size:', scaledSize);
        
        // Add user data for identification
        glbObject.userData.isFloorPlanObject = true;
        glbObject.userData.originalData = productData;
        glbObject.userData.type = 'product';
        glbObject.userData.isGLB = true;
        glbObject.userData.productId = productId;
        glbObject.userData.currentPosition = 'full up'; // Default position for new products
        
        scene.add(glbObject);
        
        // Create pocket for this shade product
        const productPosition = {
          x: glbObject.position.x,
          y: glbObject.position.y,
          z: glbObject.position.z
        };
        createPocket(productData, wallPosition, productPosition, productConfig);
        
        console.log(`Created GLB product at (${normalized.x}, ${normalized.z}) with scale ${scale}`);
        
        // Remove from loading state
        setLoadingGLBModels(prev => {
          const newSet = new Set(prev);
          newSet.delete(productId);
          return newSet;
        });
        

      },
      (progress) => {
        console.log('GLB loading progress:', (progress.loaded / progress.total * 100) + '%');
      },
      (error) => {
        console.error('Error loading GLB product model:', error);
        
        // Remove placeholder on error
        scene.remove(placeholder);
        // Remove from loading state
        setLoadingGLBModels(prev => {
          const newSet = new Set(prev);
          newSet.delete(productId);
          return newSet;
        });
        

      }
    );

  };

  const createWall = (wallData) => {
    const scene = sceneRef.current;
    
    // Get canvas dimensions for proper coordinate normalization
    const canvasDimensions = getCanvasDimensions();
    
    // Calculate wall dimensions and position from points
    const points = wallData.points;
    if (!points || points.length < 4) return;
    
    // Normalize the start and end points
    const startNormalized = normalize2DTo3D(points[0], points[1], canvasDimensions.width, canvasDimensions.height);
    const endNormalized = normalize2DTo3D(points[2], points[3], canvasDimensions.width, canvasDimensions.height);
    
    // Calculate wall length and angle
    const length = Math.sqrt(Math.pow(endNormalized.x - startNormalized.x, 2) + Math.pow(endNormalized.z - startNormalized.z, 2));
    const angle = Math.atan2(endNormalized.z - startNormalized.z, endNormalized.x - startNormalized.x);
    
    // Create wall geometry
    const wallGeometry = new THREE.BoxGeometry(length, wallHeight, 0.1); // Reduced from 0.2 to 0.1 (50% thinner)
    const wallMaterial = new THREE.MeshLambertMaterial({ 
      color: 0x666666,
      transparent: true,
      opacity: 0.9
    });
    
    const wall = new THREE.Mesh(wallGeometry, wallMaterial);
    
    // Position and rotate wall
    const centerX = (startNormalized.x + endNormalized.x) / 2;
    const centerZ = (startNormalized.z + endNormalized.z) / 2;
    wall.position.set(centerX, wallHeight / 2, centerZ);
    wall.rotation.y = angle;
    
    wall.castShadow = true;
    wall.receiveShadow = true;
    wall.userData.isFloorPlanObject = true;
    wall.userData.originalData = wallData;
    wall.userData.type = 'wall';
    
    scene.add(wall);
    
    console.log(`Created wall from (${startNormalized.x}, ${startNormalized.z}) to (${endNormalized.x}, ${endNormalized.z})`);
  };

  const resetCamera = () => {
    if (!cameraRef.current) return;
    
    // Reset to perspective view (from above/side)
    cameraRef.current.position.set(4, 3, 4);
    cameraRef.current.lookAt(0, 0, 0);
    
    // Reset controls target to origin for perspective view
    if (controlsRef.current) {
      controlsRef.current.target.set(0, 0, 0);
      controlsRef.current.update();
    }
  };

  const setTopView = () => {
    if (!cameraRef.current) return;
    
    cameraRef.current.position.set(0, 30, 0);
    cameraRef.current.lookAt(0, 0, 0);
  };

  const setSideView = () => {
    if (!cameraRef.current) return;
    
    cameraRef.current.position.set(30, 0, 0);
    cameraRef.current.lookAt(0, 0, 0);
  };

  const resetCameraFromBottom = () => {
    if (!cameraRef.current) return;
    
    // Position camera below the scene looking up at the ceiling
    // Use negative Y to be below the floor, looking up at ceiling level
    cameraRef.current.position.set(0, -5, 0);
    cameraRef.current.lookAt(0, wallHeight, 0); // Look up at ceiling level
    
    // Update controls target to ceiling level for better navigation
    if (controlsRef.current) {
      controlsRef.current.target.set(0, wallHeight, 0);
      controlsRef.current.update();
    }
  };

  const zoomIn = () => {
    if (!cameraRef.current) return;
    
    const distance = cameraRef.current.position.length();
    const newDistance = Math.max(distance * 0.9, 1);
    cameraRef.current.position.normalize().multiplyScalar(newDistance);
  };

  const zoomOut = () => {
    if (!cameraRef.current) return;
    
    const distance = cameraRef.current.position.length();
    const newDistance = Math.min(distance * 1.1, 100);
    cameraRef.current.position.normalize().multiplyScalar(newDistance);
  };

  // Expose methods to parent component
  useImperativeHandle(ref, () => ({
    resetCamera,
    resetCameraFromBottom,
    setTopView,
    setSideView,
    zoomIn,
    zoomOut,
    setWallHeight,
    wallHeight
  }));

  return (
    <Box
      position="relative"
      width="100%"
      height="100%"
      overflow="hidden"
    >
      <div 
        ref={mountRef}
        style={{
          width: '100%',
          height: '100%',
          border: '1px solid #e2e8f0',
          borderRadius: '8px'
        }}
      />
      {(!floorPlanData || !Array.isArray(floorPlanData.objects) || floorPlanData.objects.length === 0) && (
        <Box position="absolute" top="50%" left="50%" transform="translate(-50%, -50%)" color="gray.500" zIndex={1} fontSize="lg">
          No rooms to display in 3D.
        </Box>
      )}
      
      {/* Loading indicator for GLB models */}
      {loadingGLBModels.size > 0 && (
        <Box 
          position="absolute" 
          top="20px" 
          right="20px" 
          bg="blue.500" 
          color="white" 
          px={3} 
          py={2} 
          borderRadius="md" 
          zIndex={2}
          fontSize="sm"
        >
          Loading {loadingGLBModels.size} 3D model{loadingGLBModels.size > 1 ? 's' : ''}...
        </Box>
      )}
      
      {/* Color picker modal */}
      <Modal isOpen={isColorModalOpen} onClose={() => setIsColorModalOpen(false)}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Product Customization</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4} align="stretch">
              <Box>
                <Text mb={2} fontWeight="semibold">Product: {selectedProduct?.userData?.originalData?.name || 'Unknown'}</Text>
                <Text fontSize="sm" color="gray.600" mb={1}>
                  Available Materials: {selectedProduct ? Array.from(getAvailableMaterials(selectedProduct).keys()).join(', ') : 'None'}
                </Text>
                <Text fontSize="xs" color="blue.600" fontStyle="italic">
                  Colors are applied to PBR material albedo properties
                </Text>
              </Box>
              
              <Box>
                <Text mb={2} fontWeight="semibold">Color 1 - Nickel Material:</Text>
                <HStack>
                  <Input
                    type="color"
                    value={nickelColor}
                    onChange={(e) => setNickelColor(e.target.value)}
                    w="60px"
                    h="40px"
                    p={1}
                    border="2px solid"
                    borderColor="gray.200"
                    borderRadius="md"
                  />
                  <Input
                    type="text"
                    value={nickelColor}
                    onChange={(e) => setNickelColor(e.target.value)}
                    placeholder="#C0C0C0"
                    maxW="120px"
                  />
                  <Text fontSize="sm" color="gray.600">Nickel</Text>
                </HStack>
              </Box>
              
              <Box>
                <Text mb={2} fontWeight="semibold">Color 2 - NeedlePunchWhite Material:</Text>
                <HStack>
                  <Input
                    type="color"
                    value={needlePunchColor}
                    onChange={(e) => setNeedlePunchColor(e.target.value)}
                    w="60px"
                    h="40px"
                    p={1}
                    border="2px solid"
                    borderColor="gray.200"
                    borderRadius="md"
                  />
                  <Input
                    type="text"
                    value={needlePunchColor}
                    onChange={(e) => setNeedlePunchColor(e.target.value)}
                    placeholder="#FFFFFF"
                    maxW="120px"
                  />
                  <Text fontSize="sm" color="gray.600">NeedlePunchWhite</Text>
                </HStack>
              </Box>
              
              <Box>
                <Text mb={2} fontWeight="semibold">Position:</Text>
                <Select 
                  value={selectedPosition} 
                  onChange={(e) => setSelectedPosition(e.target.value)}
                  bg="white"
                >
                  <option value="full up">Full Up (Original Height)</option>
                  <option value="half way">Half Way (5 ft Height)</option>
                  <option value="full down">Full Down (10 ft Height)</option>
                </Select>
                <Text fontSize="xs" color="gray.500" mt={1}>
                  Changing position will scale the fabric height
                </Text>
              </Box>
              
              <Box pt={2} borderTop="1px solid" borderColor="gray.200">
                <Checkbox
                  isChecked={applyToAll}
                  onChange={(e) => setApplyToAll(e.target.checked)}
                  colorScheme="blue"
                  size="md"
                >
                  <Text fontWeight="semibold">Apply to all products</Text>
                </Checkbox>
                <Text fontSize="xs" color="gray.500" mt={1}>
                  When checked, colors and position will be applied to all product objects in the scene
                  {applyToAll && ` (${getAllProductObjects().length} products will be updated)`}
                </Text>
              </Box>
            </VStack>
          </ModalBody>
          
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={() => setIsColorModalOpen(false)}>
              Cancel
            </Button>
            <Button colorScheme="blue" onClick={handleApplyChanges}>
              {applyToAll ? `Apply to All Products (${getAllProductObjects().length})` : 'Apply Changes'}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
});

ThreeDViewer.displayName = 'ThreeDViewer';

export default ThreeDViewer; 
